import { Box, Button, Grid } from "@mui/material";
import React, { useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import AddEditDocsRestrictedDialog from "../../components/dialog/AddEditDocsRestrictedDialog/AddEditDocsRestrictedDialog";
import { useDocsTableConfig } from "../../components/table/DocsTable/TablesConfig";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { useAuth } from "../../context/auth";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER, USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import {
    documentsApi,
    useAllInvestmentDocumentsWithDetails,
    useDeleteDocumentFromAllInvests,
} from "../../services/documentsService";
import TableService from "../../services/tableService";
import { NoAccessPage } from "../NoAccessPage/NoAccessPage";
import { SelectInvestmentsForDocumentDialog } from "../InvestmentProspectPage/SelectInvestmentsForDocumentDialog";
import { useDocsFilteringData } from "../../services/documentsService";

export function InvestmentDocsPage(props) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const axiosPrivate = useAxiosPrivate();

    const [investmentSelectionDialogOpen, setInvestmentSelectionDialogOpen] = useState(false);
    const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false);
    const [selectedInvestmentIds, setSelectedInvestmentIds] = useState([]);
    const { data: filteringData, isLoading: filteringDataLoading } = useDocsFilteringData();

    const {
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const disableInvestmentFilter = searchParams.get('disable_investment_filter')

    const { getTableProps, fetchingFromBackend } = useDocsTableConfig();

    const {
        data: documentsData,
        isLoading: documentsLoading,
        refetch: refetchDocuments,
    } = useAllInvestmentDocumentsWithDetails(searchParams);

    const deleteMutation = useDeleteDocumentFromAllInvests({
        onSuccess: () => {
            toast.success(t("documents.document_deleted_successfully"));
            refetchDocuments();
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_delete"));
        },
    });

    const tableProps = useMemo(() => {
        if (fetchingFromBackend) {
            return undefined;
        }
        return getTableProps(
            { excludedFields: ["manage_products", "docs_nr", "docs_parent_nr", "docs_revision_nr", "docs_restricted_user", "docs_restricted_role"], 
              readOnlyFilters: disableInvestmentFilter ? ["investment"] : [] 
        });
    }, [fetchingFromBackend, getTableProps]);

    const handleAddFile = () => {
        setInvestmentSelectionDialogOpen(true);
    };

    const handleInvestmentSelectionConfirm = (investIds) => {
        setSelectedInvestmentIds(investIds);
        setInvestmentSelectionDialogOpen(false);
        setAddDocumentDialogOpen(true);
    };

    const handleProductSelectionClose = () => {
        setInvestmentSelectionDialogOpen(false);
    };

    const handleAddDocumentDialogClose = () => {
        setAddDocumentDialogOpen(false);
        setSelectedInvestmentIds([]);
    };

    const handleAddDocumentSubmit = (uploadedDocIds) => {
        toast.success(t("documents.documents_uploaded_successfully"));
        refetchDocuments();
        setAddDocumentDialogOpen(false);
        setSelectedInvestmentIds([]);
    };

    const handleDownloadDoc = async (docId) => {
        try {
            await documentsApi.handleDownloadDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_download"));
        }
    };

    const handlePreviewDoc = async (docId) => {
        try {
            await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_preview"));
        }
    };

    const onPreview = (fileId, index) => {
        if (fileId) {
            handlePreviewDoc(fileId);
        } else if (index !== undefined && documentsData?.[index]) {
            const doc = documentsData[index];
            handlePreviewDoc(doc.id);
        }
    };

    const onDownloadFile = (fileId, fileName) => {
        handleDownloadDoc(fileId);
    };

    const onDeleteFile = (fileId) => {
        deleteMutation.mutate({ document_id: fileId });
    };

    const transformedDocsData = useMemo(() => {
        if (!documentsData) {
            return [];
        }

        return documentsData.map((doc) => ({
            ...doc,
            related_invests: doc.related_invests || [],
        }));
    }, [documentsData]);

    const preparedDocsData = useMemo(() => {
        if (!tableProps?.tableConfig || !transformedDocsData) {
            return undefined;
        }

        return TableService.getPreparedDataForCollapsibleTable(
            tableProps.tableConfig,
            transformedDocsData,
            [],
            [],
            onPreview,
            onDownloadFile,
            onDeleteFile,
            (rowData) => rowData.is_prospectus, // readOnly
            [DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER], // availableDocsRestrictedTypes
            refetchDocuments,
            (rowData) => !rowData.is_prospectus, // showEditButton
            (rowData) => !rowData.is_prospectus,  // showRevisionButton
            undefined, // index
            {}, //context
            true, // showRevisionHistoryButton
        );
    }, [tableProps?.tableConfig, transformedDocsData, refetchDocuments]);

    const toolbarComponent = () => (
        <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
                <Button variant="contained" onClick={handleAddFile}>
                    {t("documents.DocumentsPage.addInvestmentDocs", "Add Document")}
                </Button>
            </Grid>
        </Grid>
    );

    const tableComponent = () => (
        <Box sx={{ width: "100%" }}>
            {tableProps && preparedDocsData && (
                <FilterTable
                    // @ts-ignore
                    data={preparedDocsData}
                    withPagination={true}
                    page={dataPage}
                    pageSize={dataPageSize}
                    totalCount={documentsData?.length || 0}
                    onPageChange={handleChangeDataPage}
                    onPageSizeChange={handleChangeDataPageSize}
                    style={{ maxHeight: "70vh" }}
                    showCleanFilterIcon={!disableInvestmentFilter}
                    showContextMenu={false}
                    showExportToFileButton={false}
                    headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                        tableProps.tableConfig,
                        { loading: filteringDataLoading, value: filteringData },
                        [],
                        [],
                    )}
                    showDetailsIcon={false}
                    showCounterRecords={false}
                    showCheckbox={false}
                    // filterPrefix={tableProps.tableFilterPrefix}
                    setSelectedRowId={() => {}}
                    isLoading={documentsLoading}
                />
            )}
        </Box>
    );

    const { hasAccess } = useAccessControl([USER_ROLES.ADMIN, USER_ROLES.SALES_MANAGER, USER_ROLES.SALES, USER_ROLES.SALES_LEGAL, USER_ROLES.SALES_MARKETING]);

    if (!hasAccess) {
        return <NoAccessPage />;
    }

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.investment_docs")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item container direction="row" alignItems="center" spacing={3}>
                    <Grid item xs={12}>
                        {toolbarComponent()}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <LoaderWrapper showLoader={filteringDataLoading}>{tableComponent()}</LoaderWrapper>
                </Grid>
            </Grid>
            {investmentSelectionDialogOpen && (
                <SelectInvestmentsForDocumentDialog
                    open={investmentSelectionDialogOpen}
                    onClose={handleProductSelectionClose}
                    onConfirm={handleInvestmentSelectionConfirm}
                />
            )}
            {addDocumentDialogOpen && (
                <AddEditDocsRestrictedDialog
                    open={addDocumentDialogOpen}
                    onClose={handleAddDocumentDialogClose}
                    onSubmit={handleAddDocumentSubmit}
                    request_user={user?.username || ""}
                    multiple={true}
                    showIndustry={true}
                    availableDocsRestrictedTypes={[DOCS_RESTRICTED_USER, DOCS_RESTRICTED_ROLE]}
                    readOnly={false}
                    filesOnMemory={false}
                    isRevisionMode={false}
                    invest_ids={selectedInvestmentIds}
                    isProspectus={false}
                />
            )}
        </DefaultPageWrapper>
    );
}
